import type { FC, ReactNode } from 'react'
import type { Formation, Part } from '@/prakrit/lib/grammar/formation'

import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'

const partClass = (part: Part) => {
    if (part.role === 'ending') return 'text-terracotta'
    if (part.role === 'changed') return 'text-saffron-strong'
    if (part.role === 'plus') return 'text-muted-foreground font-sans font-normal'

    return 'text-prakrit'
}

export const FormulaParts: FC<{ parts: Part[], className?: string }> = ({ parts, className }): ReactNode => (
    <span lang='pra' className={cn('font-prakrit font-semibold', className)}>
        {parts.map((part, i) => (
            <span key={i} className={partClass(part)}>{part.text}</span>
        ))}
    </span>
)

interface FormationStepsProps {
    formation: Formation
    className?: string
    size?: 'sm' | 'md'
    /** Show only the steps between root and result (the root and the finished form are drawn by the caller). */
    compact?: boolean
}

/** Rule-aware build-up of a form: पढ → पढ + मि → पढा + मि (मि से पहले अ → आ) → पढामि. */
export const FormationSteps: FC<FormationStepsProps> = ({ formation, className, size = 'md', compact = false }): ReactNode => {
    const steps = compact ? formation.steps.slice(1, -1) : formation.steps

    return (
        <ol className={cn('flex flex-col gap-2', className)}>
            {steps.map((step, i) => (
                <li key={i} className='flex items-start gap-3'>
                    <span aria-hidden='true' className='mt-1.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground'>
                        {i + 1}
                    </span>
                    <span className='flex min-w-0 flex-col'>
                        <FormulaParts parts={step.parts} className={size === 'md' ? 'pra-md' : 'text-lg'} />
                        <Bi hi={step.note.hi} en={step.note.en} layout='inline' className='text-sm text-muted-foreground' />
                    </span>
                </li>
            ))}
            {formation.rule && (
                <li className='pl-9 text-xs text-muted-foreground'>
                    <span lang='hi' className='hindi'>नियम: {formation.rule}</span>
                    <span lang='en'> · प्राकृत स्वयं-शिक्षक</span>
                </li>
            )}
        </ol>
    )
}
